import { instance, headers, unwrapList, get, monthRange, fetchPaged } from "./shared";
import {
  fetchDeliveryNotesLive,
  mapVehicleDeliveryNote,
  summarizeDeliveryNotes,
} from "./deliveryNotes";

const looksLikeUpcomingMaintenance = (payload) =>
  payload &&
  typeof payload === "object" &&
  !Array.isArray(payload) &&
  (payload.next7Days !== undefined ||
    payload.next30Days !== undefined ||
    payload.next60Days !== undefined);

const normalizeUpcomingMaintenance = (payload, windowKey = 30) => {
  const windowData =
    payload?.[`next${windowKey}Days`] || payload?.[`next${windowKey}`] || {};
  const services = Object.entries(windowData)
    .filter(
      ([, value]) => typeof value === "number" || typeof value === "string"
    )
    .map(([name, count]) => ({
      name,
      count: Number(count) || 0,
    }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);

  return {
    services,
    vehiclesAffected: services.reduce((sum, item) => sum + item.count, 0),
    estimatedCost: Number(payload?.estimatedCost ?? 0) || 0,
  };
};

const startOfDayIso = (date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value.toISOString();
};

const endOfDayIso = (date) => {
  const value = new Date(date);
  value.setHours(23, 59, 59, 999);
  return value.toISOString();
};

const lastDaysRange = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: startOfDayIso(from), to: endOfDayIso(to) };
};

const nextDaysRange = (days) => {
  const from = new Date();
  const to = new Date();
  to.setDate(to.getDate() + days);
  return { from: startOfDayIso(from), to: endOfDayIso(to) };
};

const emptyPredictionBucket = () => ({
  vehicles: 0,
  serviceCount: 0,
  potential: 0,
  items: [],
});

const emptyPredictions = () => ({
  ...emptyPredictionBucket(),
  pending: emptyPredictionBucket(),
  completed: emptyPredictionBucket(),
});

const mapPredictionGroup = (group) => ({
  plateNumber: group?.plateNumber || group?.plateNo || "",
  count: Number(group?.count) || 0,
  totalPotential: Number(group?.totalPotential) || 0,
  contractNumber: group?.contractNumber || group?.cn || "",
});

/** Same source as Fleet Health "Upcoming Maintenance": GET /api/dashboards/fmd/19 */
export async function fetchUpcomingMaintenanceLive(params = {}) {
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
  const url = query
    ? `/api/dashboards/fmd/19?${query}`
    : `/api/dashboards/fmd/19`;

  try {
    const res = await instance({ url, method: "GET", headers });
    if (looksLikeUpcomingMaintenance(res)) return res;
    if (looksLikeUpcomingMaintenance(res?.data)) return res.data;
    return {};
  } catch (err) {
    if (looksLikeUpcomingMaintenance(err)) return err;
    if (looksLikeUpcomingMaintenance(err?.data)) return err.data;
    throw err;
  }
}

/** Same source as Missed Opportunities AI PMS Due (status=1) / Schedule (status=2) */
export async function fetchPredictionsGroupedLive({
  status,
  from,
  to,
  ctx,
  completed = 0,
} = {}) {
  const cn = ctx?.ServerSetting?.contractNumber || "";
  const isOwner = Number(ctx?.logInUser?.userType) === -1;
  let apiTotal = 0;
  const items = [];

  for (let page = 1; page <= 6; page += 1) {
    const params = [
      `page=${page}`,
      `limit=1000`,
      `status=${status}`,
      `completed=${completed}`,
      `wanted=1`,
    ];
    if (isOwner) params.push("all=true");
    if (cn) params.push(`cn=${encodeURIComponent(cn)}`);
    if (from) params.push(`from=${encodeURIComponent(from)}`);
    if (to) params.push(`to=${encodeURIComponent(to)}`);

    const res = await get(`/api/predictions/getGrouped?${params.join("&")}`);
    const chunk = unwrapList(res);
    if (page === 1) {
      apiTotal = Number(res?.total ?? chunk.length) || 0;
    }
    items.push(...chunk);
    if (!res?.hasNext || !chunk.length) break;
  }

  return {
    vehicles: apiTotal || items.length,
    serviceCount: items.reduce((sum, item) => sum + (Number(item.count) || 0), 0),
    potential: items.reduce(
      (sum, item) => sum + (Number(item.totalPotential) || 0),
      0
    ),
    items: items.slice(0, 15).map(mapPredictionGroup),
  };
}

const mergePredictionBuckets = (pending, done) => ({
  vehicles: (pending.vehicles || 0) + (done.vehicles || 0),
  serviceCount: (pending.serviceCount || 0) + (done.serviceCount || 0),
  potential: (pending.potential || 0) + (done.potential || 0),
  pending,
  completed: done,
  items: [
    ...pending.items.map((item) => ({ ...item, completed: 0 })),
    ...done.items.map((item) => ({ ...item, completed: 1 })),
  ],
});

export async function fetchPredictionsSplit({ status, from, to, ctx } = {}) {
  const results = await Promise.allSettled([
    fetchPredictionsGroupedLive({ status, from, to, ctx, completed: 0 }),
    fetchPredictionsGroupedLive({ status, from, to, ctx, completed: 1 }),
  ]);

  if (results.every((item) => item.status === "rejected")) {
    throw results[0].reason;
  }

  const pending =
    results[0].status === "fulfilled"
      ? results[0].value
      : emptyPredictionBucket();
  const done =
    results[1].status === "fulfilled"
      ? results[1].value
      : emptyPredictionBucket();

  return mergePredictionBuckets(pending, done);
}

export async function fetchFleetHealthServiceSources(
  ctx,
  { upcoming = true, due = true, schedule = true } = {}
) {
  const dueRange = lastDaysRange(30);
  const scheduleRange = nextDaysRange(30);
  const results = await Promise.allSettled([
    upcoming ? fetchUpcomingMaintenanceLive() : Promise.resolve({}),
    due
      ? fetchPredictionsSplit({
          status: 1,
          from: dueRange.from,
          to: dueRange.to,
          ctx,
        })
      : Promise.resolve(emptyPredictions()),
    schedule
      ? fetchPredictionsSplit({
          status: 2,
          from: scheduleRange.from,
          to: scheduleRange.to,
          ctx,
        })
      : Promise.resolve(emptyPredictions()),
  ]);

  const requested = [
    upcoming && results[0],
    due && results[1],
    schedule && results[2],
  ].filter(Boolean);

  if (
    requested.length &&
    requested.every((item) => item.status === "rejected")
  ) {
    throw requested[0].reason;
  }

  const upcomingPayload =
    results[0].status === "fulfilled" ? results[0].value : {};

  return {
    upcoming: normalizeUpcomingMaintenance(upcomingPayload, 30),
    aiPmsDue:
      results[1].status === "fulfilled" ? results[1].value : emptyPredictions(),
    aiPmsSchedule:
      results[2].status === "fulfilled" ? results[2].value : emptyPredictions(),
  };
}

const COMPLETED_STATUSES = new Set(["completed", "Completed", "complete"]);

export const mapService = (service) => {
  const plate =
    service?.vehicleLicensePlate ||
    service?.vehicles?.vehicleLicensePlate ||
    service?.vehicles?.vehicleLP ||
    "";
  const vehicleName =
    service?.vehicles?.label ||
    service?.vehicleName ||
    [service?.vehicles?.attributes?.make, service?.vehicles?.attributes?.model]
      .filter(Boolean)
      .join(" ");
  const due = service?.expireTime || service?.serviceTime;
  const dueDate = due ? new Date(due) : null;
  const now = new Date();
  const daysOverdue =
    dueDate && dueDate < now
      ? Math.max(0, Math.round((now - dueDate) / 86400000))
      : 0;

  return {
    id: service?.id,
    name: service?.name || service?.workOrderNumber || "Service",
    service: service?.name || service?.workOrderNumber || "Service",
    date: service?.serviceTime || service?.createdTime,
    dueDate: due,
    expireTime: service?.expireTime,
    cost: Number(service?.cost) || 0,
    estimatedCost: Number(service?.cost) || 0,
    odometer: Number(service?.odometer) || 0,
    vehicleId: service?.vehicleId || service?.vehicles?.id,
    vehicleName,
    plateNumber: plate,
    status: service?.status || "",
    daysOverdue,
  };
};

const isCompleted = (item) =>
  COMPLETED_STATUSES.has(String(item?.status || "").trim());

const isOverdue = (item) => {
  if (String(item?.status || "").toLowerCase() === "expired") return true;
  if (isCompleted(item)) return false;
  if (!item?.expireTime) return false;
  return new Date(item.expireTime) < new Date();
};

const isUpcoming = (item) => {
  if (isCompleted(item) || isOverdue(item)) return false;
  const due = item?.dueDate || item?.expireTime || item?.date;
  if (!due) return false;
  return new Date(due) >= new Date();
};

export async function fetchServicesLive({
  vehicleId,
  from,
  to,
  status,
  limit = 50,
} = {}) {
  const items = await fetchPaged(
    (page, pageSize) => {
      const params = [`page=${page}`, `limit=${pageSize}`];
      if (vehicleId) params.push(`vehicleId=${vehicleId}`);
      if (from) params.push(`from=${encodeURIComponent(from)}`);
      if (to) params.push(`to=${encodeURIComponent(to)}`);
      if (status) params.push(`status=${encodeURIComponent(status)}`);
      return `/api/services/get?${params.join("&")}`;
    },
    { limit, maxPages: vehicleId ? 4 : 6 }
  );
  return items.map(mapService);
}

export async function loadServicesQuestion(questionId, ctx) {
  switch (questionId) {
    case "servicesThisMonth": {
      const { upcoming, aiPmsDue, aiPmsSchedule } =
        await fetchFleetHealthServiceSources(ctx);
      return {
        month: {
          upcoming,
          aiPmsDue,
          aiPmsSchedule,
          totalServices:
            upcoming.vehiclesAffected +
            aiPmsDue.serviceCount +
            aiPmsSchedule.serviceCount,
          totalServiceCost:
            upcoming.estimatedCost +
            aiPmsDue.potential +
            aiPmsSchedule.potential,
          vehiclesServiced:
            upcoming.vehiclesAffected +
            aiPmsDue.vehicles +
            aiPmsSchedule.vehicles,
        },
      };
    }

    case "upcomingServices": {
      const { upcoming, aiPmsSchedule } = await fetchFleetHealthServiceSources(
        ctx,
        { due: false }
      );
      return { upcoming, aiPmsSchedule };
    }

    case "overdueServices": {
      const { aiPmsDue } = await fetchFleetHealthServiceSources(ctx, {
        upcoming: false,
        schedule: false,
      });
      return { aiPmsDue };
    }

    case "serviceHistory": {
      const last = monthRange(-1);
      const notes = await fetchDeliveryNotesLive({
        from: last.from,
        to: last.to,
        limit: 1000,
        maxPages: 1,
      });
      const sorted = [...notes].sort(
        (a, b) =>
          new Date(b.serviceDate || b.createdTime || 0) -
          new Date(a.serviceDate || a.createdTime || 0)
      );
      return {
        list: sorted.map(mapVehicleDeliveryNote),
        summary: { label: last.label, ...summarizeDeliveryNotes(notes) },
      };
    }

    default:
      return null;
  }
}
